import { useMemo, useState } from 'react'

import { useLightMode } from '@/hooks/useLightMode'
import { Controller, ControllerRenderProps } from 'react-hook-form'
import Select from 'react-select'
import CreatableSelect from 'react-select/creatable'

import { Box, FormErrorMessage, Text } from '@chakra-ui/react'
import { FormControl as ChakraFormControl } from '@chakra-ui/react'

import { OptionI } from '@/utils/forms/optionBuilders'

import {
  CheckboxOption,
  CustomOption,
  DropdownIndicator,
  HiddenPlaceholder,
  MenuListWithSelectAll,
  MultiValue,
  SingleValue,
  ValueContainer,
} from './SubComponents'
import { createCustomStyles } from './styles'
import { FormInputSelectControlV2IProps } from './types'
import { handleChipRemove, handleSelectAll, handleSelectChange } from './utils'

export const FormInputSelectControlV2 = ({
  id,
  control,
  options,
  defaultValue,
  placeholder = 'Select...',
  isMulti = false,
  canRemoveSelectedOption = true,
  noOptionsMessage,
  onInputChange,
  showCheckboxes = false,
  showSelectAll = false,
  showAllSelectedChip = false,
  allSelectedLabel = 'All',
  showSubtitle = false,
  filterOption,
  canCreateOption = false,
  formatCreateLabel,
  isValidNewOption,
  onCreateOption,
  label,
  showRequired = false,
  requiredLabel = 'REQUIRED',
  selectedLabel,
  rules,
  isInvalid = false,
  errorMessage,
  isDisabled = false,
  isLoading = false,
  dataTestId,
  width = '100%',
}: FormInputSelectControlV2IProps) => {
  const mode = useLightMode()
  const chipTextColor = `text.count-badge.${mode}`
  const bgColor = `bg.input.${mode}`
  const borderHover = `border.input-hover.${mode}`
  const borderFocus = `border.input-focus.${mode}`
  const textColor = `text.primary.${mode}`

  const customStyles = useMemo(() => createCustomStyles(mode), [mode])

  // Track input value for filtering
  const [inputValue, setInputValue] = useState('')

  // Compute filtered options based on inputValue
  const filteredOptions = useMemo(() => {
    if (!inputValue) return options

    if (filterOption) {
      return options.filter((opt) => filterOption({ data: opt }, inputValue))
    }

    // Default: case-insensitive label match
    const lowerInput = inputValue.toLowerCase()
    return options.filter((opt) => opt.label.toLowerCase().includes(lowerInput))
  }, [options, inputValue, filterOption])

  const selectComponents = useMemo(
    () => ({
      DropdownIndicator,
      IndicatorSeparator: null,
      Placeholder: HiddenPlaceholder,
      SingleValue,
      MultiValue,
      ValueContainer,
      ...(showCheckboxes
        ? { Option: CheckboxOption }
        : { Option: CustomOption }),
      ...(showSelectAll && isMulti && { MenuList: MenuListWithSelectAll }),
    }),
    [showCheckboxes, showSelectAll, isMulti, options]
  )

  const SelectComponent = (
    canCreateOption ? CreatableSelect : Select
  ) as typeof Select

  return (
    <ChakraFormControl
      data-testid={dataTestId}
      isDisabled={isDisabled}
      isInvalid={isInvalid}
      width={width}
    >
      <Controller
        control={control}
        defaultValue={defaultValue}
        name={id}
        render={({ value, onChange }: ControllerRenderProps) => {
          // Value is now OptionI | OptionI[] | null, use directly
          const selectedValue = value

          return (
            <Box
              bgColor={bgColor}
              borderColor={isInvalid ? '#A62630' : 'transparent'}
              borderWidth='1px'
              position='relative'
              px='12px'
              py='8px'
              sx={{
                '&:hover:not(:focus-within)': {
                  borderColor: isInvalid ? '#A62630' : borderHover,
                },
                '&:focus-within': {
                  borderColor: borderFocus,
                },
              }}
            >
              {label && (
                <Text
                  color={`text.input-value.${mode}`}
                  fontSize='10px'
                  fontWeight='500'
                  mb='8px'
                  opacity={0.8}
                  textTransform='uppercase'
                >
                  {label}
                </Text>
              )}
              {showRequired && (
                <Text
                  color={`text.input-value.${mode}`}
                  fontSize='10px'
                  fontWeight='500'
                  letterSpacing='0.5px'
                  position='absolute'
                  right='12px'
                  textTransform='uppercase'
                  top='8px'
                >
                  {requiredLabel}
                </Text>
              )}

              <SelectComponent
                allSelectedLabel={allSelectedLabel}
                blurInputOnSelect={!isMulti}
                canRemoveSelectedOption={canRemoveSelectedOption}
                chipBgColor={textColor}
                chipTextColor={chipTextColor}
                closeMenuOnSelect={!isMulti}
                components={selectComponents}
                filterOption={filterOption}
                filteredOptions={filteredOptions}
                formatCreateLabel={formatCreateLabel}
                hideSelectedOptions={false}
                isDisabled={isDisabled}
                isLoading={isLoading}
                isMulti={isMulti}
                isValidNewOption={isValidNewOption}
                menuPlacement='auto'
                noOptionsMessage={noOptionsMessage}
                onChange={(selected: OptionI | OptionI[] | null) =>
                  handleSelectChange(selected, isMulti, onChange)
                }
                onClearAll={() => onChange([])}
                onCreateOption={onCreateOption}
                onInputChange={(newValue, actionMeta) => {
                  setInputValue(newValue)
                  onInputChange?.(newValue, actionMeta)
                }}
                onRemoveChip={(valueToRemove) =>
                  handleChipRemove(valueToRemove, value, isMulti, onChange)
                }
                onSelectAll={() =>
                  handleSelectAll(filteredOptions, value, onChange)
                }
                options={options}
                placeholder={placeholder}
                selectedLabel={selectedLabel}
                showAllSelectedChip={showAllSelectedChip}
                showSelectAll={showSelectAll && isMulti}
                showSubtitle={showSubtitle}
                styles={customStyles}
                value={selectedValue}
              />
            </Box>
          )
        }}
        rules={rules}
      />
      <FormErrorMessage data-testid='form-control-select-v2-error'>
        {errorMessage}
      </FormErrorMessage>
    </ChakraFormControl>
  )
}
